// Deterministic randomness.
//
// The engine must be reproducible: the same seed and the same action sequence
// have to produce the same game, or replays, AI search and test failures all
// stop meaning anything. So the seed lives in GameState and every draw of
// randomness advances it in place -- nothing here touches Math.random.

/** splitmix32. Small, fast, and good enough for shuffling a 40 card deck. */
export function nextRandom(state: { seed: number }): number {
  state.seed = (state.seed + 0x9e3779b9) | 0;
  let z = state.seed;
  z = Math.imul(z ^ (z >>> 16), 0x21f0aaad);
  z = Math.imul(z ^ (z >>> 15), 0x735a2d97);
  z = z ^ (z >>> 15);
  return (z >>> 0) / 4294967296;
}

/** Uniform integer in [0, bound). */
export function nextInt(state: { seed: number }, bound: number): number {
  if (bound <= 1) return 0;
  return Math.floor(nextRandom(state) * bound);
}

/** Fisher-Yates, in place. */
export function shuffle<T>(state: { seed: number }, items: T[]): T[] {
  for (let i = items.length - 1; i > 0; i--) {
    const j = nextInt(state, i + 1);
    const a = items[i]!;
    items[i] = items[j]!;
    items[j] = a;
  }
  return items;
}

export function pick<T>(state: { seed: number }, items: readonly T[]): T | undefined {
  if (items.length === 0) return undefined;
  return items[nextInt(state, items.length)];
}

/** Turns an arbitrary string into a usable seed, for "seed: my-game" style input. */
export function seedFrom(text: string): number {
  let h = 0x811c9dc5;
  for (let i = 0; i < text.length; i++) {
    h ^= text.charCodeAt(i);
    h = Math.imul(h, 0x01000193);
  }
  return h >>> 0;
}
